// @flow

import _ from 'lodash';
import { DEFAULT_PACKAGE_DATA } from 'src/constants/subscription-v5';

import {
  VERIFY_DISCOUNT_CODE_REQUEST,
  VERIFY_DISCOUNT_CODE_SUCCESS,
  VERIFY_DISCOUNT_CODE_FAILURE,
  SUBSCRIBE_PLAN_REQUEST,
  SUBSCRIBE_PLAN_SUCCESS,
  SUBSCRIBE_PLAN_FAILURE,
  ACTIVATE_CHARGE_REQUEST,
  ACTIVATE_CHARGE_SUCCESS,
  ACTIVATE_CHARGE_FAILURE,
  CLEAR_DISCOUNT_CODE,
  STORE_CURRENT_ROUTE_SUCCESS,
  GET_GMV_LIMIT_REQUEST,
  GET_GMV_LIMIT_SUCCESS,
  GET_GMV_LIMIT_FAILURE,
  GET_GMV_LIMIT_PLANS_REQUEST,
  GET_GMV_LIMIT_PLANS_SUCCESS,
  GET_GMV_LIMIT_PLANS_FAILURE,
  GET_GMV_BILLING_HISTORY_REQUEST,
  GET_GMV_BILLING_HISTORY_SUCCESS,
  GET_GMV_BILLING_HISTORY_FAILURE,
  GET_INVOICE_PLAN_REQUEST,
  GET_INVOICE_PLAN_SUCCESS,
  GET_INVOICE_PLAN_FAILURE,
  SubscriptionState,
  SubscriptionAction,
  VALIDATE_GMV_LIMIT_REQUEST,
  VALIDATE_GMV_LIMIT_SUCCESS,
  VALIDATE_GMV_LIMIT_FAILURE
} from 'stores/flow-type/subscription-v5.type';

const initialState: SubscriptionState = {
  subscribing: false,
  loading: false,
  activating: false,
  subscribeSuccess: false,
  success: false,
  error: undefined,
  confirmationUrl: undefined,
  remainCycles: 0,
  migrateESCluster: false,

  // discount
  termsConditions: '',
  verifying: false,
  msgError: null,

  // gmv billing history
  gettingBillingHistory: false,
  getBillingHistorySuccess: false,
  billingHistoryData: [],
  totalGMVHistory: 0,

  // get current base gmv
  gettingBaseGMV: false,
  getGMVLimitSuccess: false,
  baseGMV: {},

  // validate gmv limit
  validating: false,
  validateSuccess: false,
  validateError: null,
  hasNewGMVLimit: false,

  // get plans
  gettingPlans: false,
  getPlansSuccess: false,
  plans: DEFAULT_PACKAGE_DATA,
  // invoice
  gettingInvoice: false,
  getInvoiceSuccess: false,
  invoice: {}
};

// Split large switch into smaller handler map to keep reducer concise

type Handler = (state: SubscriptionState, action: SubscriptionAction) => SubscriptionState;

const discountHandlers: { [string]: Handler } = {
  [VERIFY_DISCOUNT_CODE_REQUEST]: state => ({
    ...state,
    msgVerifyError: null,
    discount: {},
    verifying: true
  }),
  [VERIFY_DISCOUNT_CODE_SUCCESS]: (state, action) => {
    const { data = {} } = action.payload;
    const discount = _.get(data, 'discount', {});
    const remainCycles = _.get(data, 'remainCycles', {});
    const termsConditions = _.get(data, 'termsConditions', '');
    return {
      ...state,
      verifying: false,
      error: undefined,
      termsConditions,
      discount,
      remainCycles,
      msgVerifyError: null
    };
  },
  [VERIFY_DISCOUNT_CODE_FAILURE]: (state, action) => {
    const data = _.get(action, 'payload.data', {});
    const responseMsg = _.get(data, 'message', '');
    const splitMsg = responseMsg.split(':');
    const msg = _.get(splitMsg, '[1]', responseMsg);
    return {
      ...state,
      verifying: false,
      success: false,
      error: action.payload,
      msgError: msg.trimStart()
    };
  },
  [CLEAR_DISCOUNT_CODE]: state => ({
    ...state,
    discount: {},
    discountAddon: {},
    msgError: null,
    error: undefined
  })
};

const routeHandlers: { [string]: Handler } = {
  [STORE_CURRENT_ROUTE_SUCCESS]: (state, action) => ({
    ...state,
    backRoute: action.payload?.route || null
  })
};

const subscribeHandlers: { [string]: Handler } = {
  [SUBSCRIBE_PLAN_REQUEST]: state => ({ ...state, subscribing: true, subscribeSuccess: false }),
  [SUBSCRIBE_PLAN_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    const { confirmationUrl } = data;
    return {
      ...state,
      subscribing: false,
      subscribeSuccess: true,
      error: undefined,
      confirmationUrl
    };
  },
  [SUBSCRIBE_PLAN_FAILURE]: (state, action) => ({
    ...state,
    subscribeSuccess: false,
    subscribing: false,
    success: false,
    error: action.payload
  })
};

const chargeHandlers: { [string]: Handler } = {
  [ACTIVATE_CHARGE_REQUEST]: state => ({
    ...state,
    activating: true,
    success: false,
    error: undefined
  }),
  [ACTIVATE_CHARGE_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    const { message, migrateESCluster = false } = data;
    if (message === 'success')
      return { ...state, activating: false, error: undefined, success: true, migrateESCluster };
    return { ...state, activating: false, error: undefined, success: false, migrateESCluster };
  },
  [ACTIVATE_CHARGE_FAILURE]: (state, action) => ({
    ...state,
    activating: false,
    success: false,
    error: action.payload || 'Charge activate failed'
  })
};

const gmvLimitHandlers: { [string]: Handler } = {
  [GET_GMV_LIMIT_REQUEST]: state => ({ ...state, gettingBaseGMV: true, getGMVLimitSuccess: false }),
  [GET_GMV_LIMIT_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    return {
      ...state,
      gettingBaseGMV: false,
      getGMVLimitSuccess: true,
      error: undefined,
      baseGMV: data
    };
  },
  [GET_GMV_LIMIT_FAILURE]: (state, action) => ({
    ...state,
    gettingBaseGMV: false,
    getGMVLimitSuccess: false,
    error: action.payload
  }),
  [VALIDATE_GMV_LIMIT_REQUEST]: state => ({
    ...state,
    validating: true,
    validateSuccess: false,
    hasNewGMVLimit: false
  }),
  [VALIDATE_GMV_LIMIT_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    const oldGMVLimit = _.get(state.baseGMV || {}, 'gmv', 0);
    const newGMVLimit = _.get(data, 'gmv', 0);
    const hasNewGMVLimit = newGMVLimit > oldGMVLimit;
    return {
      ...state,
      validating: false,
      validateSuccess: true,
      error: undefined,
      baseGMV: data,
      validateError: null,
      hasNewGMVLimit
    };
  },
  [VALIDATE_GMV_LIMIT_FAILURE]: (state, action) => ({
    ...state,
    validating: false,
    validateSuccess: false,
    validateError: action.payload,
    hasNewGMVLimit: false
  })
};

const plansHandlers: { [string]: Handler } = {
  [GET_GMV_LIMIT_PLANS_REQUEST]: state => ({
    ...state,
    gettingPlans: true,
    getPlansSuccess: false
  }),
  [GET_GMV_LIMIT_PLANS_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    return { ...state, gettingPlans: false, getPlansSuccess: true, error: undefined, plans: data };
  },
  [GET_GMV_LIMIT_PLANS_FAILURE]: (state, action) => ({
    ...state,
    gettingPlans: false,
    getPlansSuccess: false,
    error: action.payload
  })
};


const invoiceHandlers: { [string]: Handler } = {
  [GET_INVOICE_PLAN_REQUEST]: state => ({
    ...state,
    gettingInvoice: true,
    getInvoiceSuccess: false
  }),
  [GET_INVOICE_PLAN_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    return {
      ...state,
      gettingInvoice: false,
      getInvoiceSuccess: true,
      error: undefined,
      invoice: data
    };
  },
  [GET_INVOICE_PLAN_FAILURE]: (state, action) => ({
    ...state,
    gettingInvoice: false,
    getInvoiceSuccess: false,
    error: action.payload
  })
};

const getBillingHistoryHandlers: { [string]: Handler } = {
  [GET_GMV_BILLING_HISTORY_REQUEST]: state => ({
    ...state,
    gettingBillingHistory: true,
    getBillingHistorySuccess: false
  }),
  [GET_GMV_BILLING_HISTORY_SUCCESS]: (state, action) => {
    const { data } = action.payload;
    const billingHistoryData = _.get(data, 'list', []);
    const total = _.get(data, 'total', 0);
    return {
      ...state,
      gettingBillingHistory: false,
      getBillingHistorySuccess: true,
      error: undefined,
      billingHistoryData,
      totalGMVHistory: total
    };
  },
  [GET_GMV_BILLING_HISTORY_FAILURE]: (state, action) => ({
    ...state,
    gettingBillingHistory: false,
    getBillingHistorySuccess: false,
    error: action.payload
  })
};
// Combine all handlers
const handlers: { [string]: Handler } = {
  ...discountHandlers,
  ...routeHandlers,
  ...subscribeHandlers,
  ...chargeHandlers,
  ...gmvLimitHandlers,
  ...getBillingHistoryHandlers,
  ...plansHandlers,
  ...invoiceHandlers
};

const subscriptionReducers = (
  state: SubscriptionState = initialState,
  action: SubscriptionAction
) => {
  const handler = handlers[action.type];
  return handler ? handler(state, action) : state;
};
export default subscriptionReducers;
